import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const BatchProcessor = ({ selectedTemplate, onBatchDataChange, onNext, onBack }) => {
  const [batchData, setBatchData] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);

  const sampleData = [
    {
      recipient_name: "John Smith",
      recipient_email: "john.smith@email.com",
      institution: "Stanford University",
      degree: "Bachelor of Science in Computer Science",
      graduation_date: "2024-05-15",
      gpa: "3.85"
    },
    {
      recipient_name: "Sarah Johnson",
      recipient_email: "sarah.johnson@email.com",
      institution: "MIT",
      degree: "Master of Science in Data Science",
      graduation_date: "2024-06-20",
      gpa: "3.92"
    },
    {
      recipient_name: "Michael Chen",
      recipient_email: "michael.chen@email.com",
      institution: "UC Berkeley",
      degree: "Bachelor of Arts in Economics",
      graduation_date: "2024-05-10",
      gpa: "3.78"
    }
  ];

  const handleFileUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setUploadProgress(0);

    // Simulate file processing
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvContent = e?.target?.result;
        const lines = csvContent?.split('\n');
        const headers = lines?.[0]?.split(',')?.map(h => h?.trim());
        
        const processedData = [];
        const processingErrors = [];

        for (let i = 1; i < lines?.length; i++) {
          if (lines?.[i]?.trim() === '') continue;
          
          const values = lines?.[i]?.split(',')?.map(v => v?.trim());
          const record = {};
          
          headers?.forEach((header, index) => {
            record[header] = values?.[index] || '';
          });

          // Validate required fields
          const missingFields = selectedTemplate?.fields?.filter(field => !record?.[field]);
          if (missingFields?.length > 0) {
            processingErrors?.push(`Row ${i}: Missing fields - ${missingFields?.join(', ')}`);
          } else {
            processedData?.push(record);
          }

          // Update progress
          setUploadProgress(Math.round((i / (lines?.length - 1)) * 100));
        }

        setBatchData(processedData);
        setErrors(processingErrors);
        onBatchDataChange(processedData);
        
      } catch (error) {
        setErrors(['Failed to parse CSV file. Please check the format.']);
      } finally {
        setIsProcessing(false);
      }
    };

    reader?.readAsText(file);
  };

  const handleUseSampleData = () => {
    setBatchData(sampleData);
    setErrors([]);
    onBatchDataChange(sampleData);
  };

  const handleRemoveRecord = (index) => {
    const updatedData = batchData?.filter((_, i) => i !== index);
    setBatchData(updatedData);
    onBatchDataChange(updatedData);
  };

  const downloadTemplate = () => {
    const headers = ['recipient_name', 'recipient_email', ...selectedTemplate?.fields];
    const csvContent = headers?.join(',') + '\n';
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedTemplate?.name?.toLowerCase()?.replace(/\s+/g, '_')}_template.csv`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    window.URL?.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <Icon name="ArrowLeft" size={20} />
        </Button>
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Batch Credential Processing</h2>
          <p className="text-muted-foreground mt-1">Upload CSV file or use sample data for multiple credentials</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Upload Section */}
        <div className="lg:col-span-2 space-y-6">
          {/* Upload Options */}
          <div className="credential-card">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="Upload" size={20} className="mr-2" />
              Upload Data
            </h3>
            
            <div className="space-y-4">
              {/* File Upload */}
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
                <h4 className="text-lg font-medium text-foreground mb-2">Upload CSV File</h4>
                <p className="text-muted-foreground mb-4">
                  Upload a CSV file with recipient data for batch processing
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef?.current?.click()}
                    iconName="Upload"
                    iconPosition="left"
                  >
                    Choose File
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={downloadTemplate}
                    iconName="Download"
                    iconPosition="left"
                  >
                    Download Template
                  </Button>
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>

              {/* Processing Progress */}
              {isProcessing && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Processing file...</span>
                    <span className="text-foreground">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Sample Data Option */}
              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-foreground">Use Sample Data</h4>
                    <p className="text-sm text-muted-foreground">
                      Load sample data to test batch processing
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleUseSampleData}
                    iconName="Zap"
                    iconPosition="left"
                  >
                    Load Sample
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Data Preview */}
          {batchData?.length > 0 && (
            <div className="credential-card">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                <Icon name="Table" size={20} className="mr-2" />
                Data Preview ({batchData?.length} records)
              </h3>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Name</th>
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Email</th>
                      {selectedTemplate?.fields?.slice(0, 2)?.map(field => (
                        <th key={field} className="text-left py-2 px-3 text-muted-foreground font-medium">
                          {field?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                        </th>
                      ))}
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchData?.slice(0, 10)?.map((record, index) => (
                      <tr key={index} className="border-b border-border hover:bg-muted/50">
                        <td className="py-2 px-3 text-foreground">{record?.recipient_name}</td>
                        <td className="py-2 px-3 text-foreground">{record?.recipient_email}</td>
                        {selectedTemplate?.fields?.slice(0, 2)?.map(field => (
                          <td key={field} className="py-2 px-3 text-foreground">
                            {record?.[field] || '-'}
                          </td>
                        ))}
                        <td className="py-2 px-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveRecord(index)}
                            iconName="Trash2"
                          >
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {batchData?.length > 10 && (
                  <div className="text-center py-4 text-muted-foreground">
                    ... and {batchData?.length - 10} more records
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Errors */}
          {errors?.length > 0 && (
            <div className="credential-card border-destructive">
              <h3 className="text-lg font-semibold text-destructive mb-4 flex items-center">
                <Icon name="AlertTriangle" size={20} className="mr-2" />
                Processing Errors ({errors?.length})
              </h3>
              <div className="space-y-2">
                {errors?.map((error, index) => (
                  <div key={index} className="text-sm text-destructive bg-destructive/10 p-2 rounded">
                    {error}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Summary Section */}
        <div className="space-y-6">
          <div className="credential-card sticky top-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="BarChart3" size={20} className="mr-2" />
              Batch Summary
            </h3>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-muted-foreground">Template:</span>
                <span className="text-foreground font-medium">{selectedTemplate?.name}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-muted-foreground">Total Records:</span>
                <span className="text-foreground font-medium">{batchData?.length}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-muted-foreground">Valid Records:</span>
                <span className="text-success font-medium">{batchData?.length}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-muted-foreground">Errors:</span>
                <span className="text-destructive font-medium">{errors?.length}</span>
              </div>

              <div className="pt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Clock" size={14} />
                  <span>Est. processing time: {Math.ceil(batchData?.length * 0.5)} seconds</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="DollarSign" size={14} />
                  <span>Blockchain fees: ~${(batchData?.length * 0.02)?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Required Fields */}
          <div className="credential-card">
            <h4 className="font-medium text-foreground mb-3">Required CSV Columns</h4>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">recipient_name</div>
              <div className="text-xs text-muted-foreground">recipient_email</div>
              {selectedTemplate?.fields?.map(field => (
                <div key={field} className="text-xs text-muted-foreground">{field}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex justify-between pt-6 border-t border-border">
        <Button variant="outline" onClick={onBack}>
          Back to Form
        </Button>
        <Button 
          onClick={onNext}
          disabled={batchData?.length === 0 || errors?.length > 0}
          iconName="ArrowRight" 
          iconPosition="right"
        >
          Process Batch ({batchData?.length} credentials)
        </Button>
      </div>
    </div>
  );
};

export default BatchProcessor;